import React, { useState, useEffect } from 'react';
import { 
  FiSave, FiEye, FiCode, FiLayout, FiSettings, FiCopy, 
  FiUpload, FiDownload, FiTrash2, FiPlus, FiMove 
} from 'react-icons/fi';

interface TemplateField {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'table' | 'image' | 'signature';
  required: boolean;
  format?: string;
  position: { x: number; y: number; width: number; height: number };
  style: {
    fontSize: number;
    fontWeight: string;
    color: string;
    textAlign: string;
    backgroundColor?: string;
    border?: string;
  };
}

interface TemplateConfig {
  id?: string;
  name: string;
  templateType: string;
  htmlTemplate: string;
  cssStyles: string;
  fieldsConfig: TemplateField[];
  headerConfig?: any;
  footerConfig?: any;
  pageSize: 'A4' | 'A3' | 'Letter';
  orientation: 'portrait' | 'landscape';
  margins: { top: number; right: number; bottom: number; left: number };
}

const defaultMargins = { top: 20, right: 20, bottom: 20, left: 20 };

const templateTypes = [
  { value: 'QUOTATION', label: 'Quotation' },
  { value: 'SALES_ORDER', label: 'Sales Order' },
  { value: 'DELIVERY_NOTE', label: 'Delivery Note' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'SALARY_SLIP', label: 'Salary Slip' },
  { value: 'PAYROLL_SUMMARY', label: 'Payroll Summary' },
  { value: 'CUSTOM_REPORT', label: 'Custom Report' }
];

export const TemplateBuilder: React.FC = () => {
  const [template, setTemplate] = useState<TemplateConfig>({
    name: '',
    templateType: 'QUOTATION',
    htmlTemplate: '',
    cssStyles: '',
    fieldsConfig: [],
    pageSize: 'A4',
    orientation: 'portrait',
    margins: defaultMargins
  });

  const [activeTab, setActiveTab] = useState<'design' | 'code' | 'preview'>('design');
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showFieldPanel, setShowFieldPanel] = useState(true);

  // Available field types for different template types
  const getAvailableFields = (templateType: string) => {
    const commonFields = [
      { id: 'company_name', name: 'Company Name', type: 'text' },
      { id: 'company_address', name: 'Company Address', type: 'text' },
      { id: 'company_logo', name: 'Company Logo', type: 'image' },
      { id: 'document_date', name: 'Document Date', type: 'date' },
      { id: 'document_number', name: 'Document Number', type: 'text' }
    ];

    const businessFields = {
      QUOTATION: [
        { id: 'customer_name', name: 'Customer Name', type: 'text' },
        { id: 'customer_address', name: 'Customer Address', type: 'text' },
        { id: 'quote_items', name: 'Quote Items', type: 'table' },
        { id: 'subtotal', name: 'Subtotal', type: 'number' },
        { id: 'tax_amount', name: 'Tax Amount', type: 'number' },
        { id: 'total_amount', name: 'Total Amount', type: 'number' },
        { id: 'valid_until', name: 'Valid Until', type: 'date' },
        { id: 'terms_conditions', name: 'Terms & Conditions', type: 'text' }
      ],
      INVOICE: [
        { id: 'customer_name', name: 'Customer Name', type: 'text' },
        { id: 'billing_address', name: 'Billing Address', type: 'text' },
        { id: 'invoice_items', name: 'Invoice Items', type: 'table' },
        { id: 'subtotal', name: 'Subtotal', type: 'number' },
        { id: 'tax_amount', name: 'Tax Amount', type: 'number' },
        { id: 'total_amount', name: 'Total Amount', type: 'number' },
        { id: 'due_date', name: 'Due Date', type: 'date' },
        { id: 'payment_terms', name: 'Payment Terms', type: 'text' }
      ],
      SALARY_SLIP: [
        { id: 'employee_name', name: 'Employee Name', type: 'text' },
        { id: 'employee_id', name: 'Employee ID', type: 'text' },
        { id: 'department', name: 'Department', type: 'text' },
        { id: 'pay_period', name: 'Pay Period', type: 'text' },
        { id: 'basic_salary', name: 'Basic Salary', type: 'number' },
        { id: 'allowances', name: 'Allowances', type: 'number' },
        { id: 'deductions', name: 'Deductions', type: 'number' },
        { id: 'net_salary', name: 'Net Salary', type: 'number' }
      ]
    };

    return [...commonFields, ...(businessFields[templateType as keyof typeof businessFields] || [])];
  };

  const addField = (fieldType: any) => {
    const newField: TemplateField = {
      id: `field_${Date.now()}`,
      name: fieldType.id,
      label: fieldType.name,
      type: fieldType.type as any,
      required: false,
      position: { x: 50, y: 50, width: 200, height: 30 },
      style: {
        fontSize: 14,
        fontWeight: 'normal',
        color: '#000000',
        textAlign: 'left'
      }
    };

    setTemplate(prev => ({
      ...prev,
      fieldsConfig: [...prev.fieldsConfig, newField]
    }));
  };

  const updateField = (fieldId: string, updates: Partial<TemplateField>) => {
    setTemplate(prev => ({
      ...prev,
      fieldsConfig: prev.fieldsConfig.map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    }));
  };

  const deleteField = (fieldId: string) => {
    setTemplate(prev => ({
      ...prev,
      fieldsConfig: prev.fieldsConfig.filter(field => field.id !== fieldId)
    }));
    setSelectedField(null);
  };

  const generateHTML = () => {
    const fields = template.fieldsConfig.map(field => {
      const style = `
        position: absolute;
        left: ${field.position.x}px;
        top: ${field.position.y}px;
        width: ${field.position.width}px;
        height: ${field.position.height}px;
        font-size: ${field.style.fontSize}px;
        font-weight: ${field.style.fontWeight};
        color: ${field.style.color};
        text-align: ${field.style.textAlign};
        ${field.style.backgroundColor ? `background-color: ${field.style.backgroundColor};` : ''}
        ${field.style.border ? `border: ${field.style.border};` : ''}
      `;

      if (field.type === 'table') {
        return `<table style="${style}" class="data-table">{{${field.name}}}</table>`;
      } else if (field.type === 'image') {
        return `<img style="${style}" src="{{${field.name}}}" alt="${field.label}" />`;
      } else {
        return `<div style="${style}">{{${field.name}}}</div>`;
      }
    }).join('\n');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          @page {
            size: ${template.pageSize} ${template.orientation};
            margin: ${template.margins.top}mm ${template.margins.right}mm ${template.margins.bottom}mm ${template.margins.left}mm;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            position: relative;
          }
          .data-table {
            width: 100%;
            border-collapse: collapse;
          }
          .data-table th, .data-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          .data-table th {
            background-color: #f2f2f2;
          }
          ${template.cssStyles}
        </style>
      </head>
      <body>
        ${fields}
      </body>
      </html>
    `;
  };

  const saveTemplate = async () => {
    try {
      const templateData = {
        ...template,
        htmlTemplate: generateHTML()
      };

      // API call to save template
      console.log('Saving template:', templateData);
      alert('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template');
    }
  };

  const previewTemplate = () => {
    const htmlContent = generateHTML();
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write(htmlContent);
      previewWindow.document.close();
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Field Panel */}
      {showFieldPanel && (
        <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Template Builder</h3>
            <p className="text-sm text-gray-600">Drag fields to design area</p>
          </div>

          {/* Template Settings */}
          <div className="p-4 border-b border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Template Settings</h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={template.name}
                  onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter template name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={template.templateType}
                  onChange={(e) => setTemplate(prev => ({ ...prev, templateType: e.target.value }))}
                >
                  {templateTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Page Size
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={template.pageSize}
                    onChange={(e) => setTemplate(prev => ({ ...prev, pageSize: e.target.value as any }))}
                  >
                    <option value="A4">A4</option>
                    <option value="A3">A3</option>
                    <option value="Letter">Letter</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Orientation
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={template.orientation}
                    onChange={(e) => setTemplate(prev => ({ ...prev, orientation: e.target.value as any }))}
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Available Fields */}
          <div className="p-4">
            <h4 className="font-medium text-gray-900 mb-3">Available Fields</h4>
            <div className="space-y-2">
              {getAvailableFields(template.templateType).map(field => (
                <button
                  key={field.id}
                  className="w-full px-3 py-2 text-left text-sm bg-gray-50 hover:bg-gray-100 
                           border border-gray-200 rounded-md transition-colors"
                  onClick={() => addField(field)}
                >
                  <div className="font-medium">{field.name}</div>
                  <div className="text-xs text-gray-500 capitalize">{field.type}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFieldPanel(!showFieldPanel)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <FiLayout className="w-5 h-5" />
              </button>
              
              <div className="flex bg-gray-100 rounded-lg p-1">
                {(['design', 'code', 'preview'] as const).map(tab => (
                  <button
                    key={tab}
                    className={`px-3 py-1 rounded-md text-sm font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={previewTemplate}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 
                         text-gray-700 rounded-md transition-colors flex items-center"
              >
                <FiEye className="w-4 h-4 mr-2" />
                Preview
              </button>
              <button
                onClick={saveTemplate}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 
                         text-white rounded-md transition-colors flex items-center"
              >
                <FiSave className="w-4 h-4 mr-2" />
                Save Template
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'design' && (
            <DesignCanvas
              template={template}
              selectedField={selectedField}
              onSelectField={setSelectedField}
              onUpdateField={updateField}
              onDeleteField={deleteField}
            />
          )}
          {activeTab === 'code' && (
            <CodeEditor
              template={template}
              onUpdateTemplate={setTemplate}
            />
          )}
          {activeTab === 'preview' && (
            <PreviewPane template={template} />
          )}
        </div>
      </div>

      {/* Properties Panel */}
      {selectedField && (
        <FieldPropertiesPanel
          field={template.fieldsConfig.find(f => f.id === selectedField)!}
          onUpdateField={(updates) => updateField(selectedField, updates)}
          onDeleteField={() => deleteField(selectedField)}
        />
      )}
    </div>
  );
};

// Design Canvas Component
const DesignCanvas: React.FC<{
  template: TemplateConfig;
  selectedField: string | null;
  onSelectField: (fieldId: string | null) => void;
  onUpdateField: (fieldId: string, updates: Partial<TemplateField>) => void;
  onDeleteField: (fieldId: string) => void;
}> = ({ template, selectedField, onSelectField, onUpdateField, onDeleteField }) => {
  return (
    <div className="h-full bg-gray-100 p-8 overflow-auto">
      <div 
        className="mx-auto bg-white shadow-lg relative"
        style={{
          width: template.pageSize === 'A4' ? 
            (template.orientation === 'portrait' ? '595px' : '842px') :
            (template.orientation === 'portrait' ? '842px' : '1191px'),
          height: template.pageSize === 'A4' ? 
            (template.orientation === 'portrait' ? '842px' : '595px') :
            (template.orientation === 'portrait' ? '1191px' : '842px'),
          minHeight: '800px'
        }}
      >
        {template.fieldsConfig.map(field => (
          <FieldElement
            key={field.id}
            field={field}
            isSelected={selectedField === field.id}
            onSelect={() => onSelectField(field.id)}
            onUpdate={(updates) => onUpdateField(field.id, updates)}
          />
        ))}
      </div>
    </div>
  );
};

// Field Element Component
const FieldElement: React.FC<{
  field: TemplateField;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<TemplateField>) => void;
}> = ({ field, isSelected, onSelect, onUpdate }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    onSelect();

    const startX = e.clientX - field.position.x;
    const startY = e.clientY - field.position.y;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        onUpdate({
          position: {
            ...field.position,
            x: e.clientX - startX,
            y: e.clientY - startY
          }
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      className={`absolute cursor-move border-2 ${
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:border-gray-300'
      }`}
      style={{
        left: field.position.x,
        top: field.position.y,
        width: field.position.width,
        height: field.position.height,
        fontSize: field.style.fontSize,
        fontWeight: field.style.fontWeight,
        color: field.style.color,
        textAlign: field.style.textAlign as any,
        backgroundColor: field.style.backgroundColor || 'transparent'
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="p-1 truncate">
        {field.type === 'table' ? '📊 Table' : 
         field.type === 'image' ? '🖼️ Image' : 
         `{{${field.name}}}`}
      </div>
    </div>
  );
};

// Code Editor Component
const CodeEditor: React.FC<{
  template: TemplateConfig;
  onUpdateTemplate: (template: TemplateConfig) => void;
}> = ({ template, onUpdateTemplate }) => {
  const [htmlCode, setHtmlCode] = useState(template.htmlTemplate);
  const [cssCode, setCssCode] = useState(template.cssStyles);

  return (
    <div className="h-full flex">
      <div className="w-1/2 border-r border-gray-200">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h4 className="font-medium text-gray-900">HTML Template</h4>
        </div>
        <textarea
          className="w-full h-full p-4 font-mono text-sm resize-none border-none focus:outline-none"
          value={htmlCode}
          onChange={(e) => setHtmlCode(e.target.value)}
          placeholder="Enter HTML template code..."
        />
      </div>
      <div className="w-1/2">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h4 className="font-medium text-gray-900">CSS Styles</h4>
        </div>
        <textarea
          className="w-full h-full p-4 font-mono text-sm resize-none border-none focus:outline-none"
          value={cssCode}
          onChange={(e) => setCssCode(e.target.value)}
          placeholder="Enter CSS styles..."
        />
      </div>
    </div>
  );
};

// Preview Pane Component
const PreviewPane: React.FC<{
  template: TemplateConfig;
}> = ({ template }) => {
  const sampleData = {
    company_name: 'WYNI AI ERP Inc.',
    company_address: '123 Business Street, Tech City, TC 12345',
    customer_name: 'John Doe',
    document_date: new Date().toLocaleDateString(),
    document_number: 'QT-2024-0001',
    subtotal: '$1,000.00',
    tax_amount: '$100.00',
    total_amount: '$1,100.00'
  };

  const processTemplate = (html: string) => {
    let processed = html;
    Object.entries(sampleData).forEach(([key, value]) => {
      processed = processed.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return processed;
  };

  return (
    <div className="h-full bg-gray-100 p-8 overflow-auto">
      <div 
        className="mx-auto bg-white shadow-lg"
        dangerouslySetInnerHTML={{ 
          __html: processTemplate(template.htmlTemplate || '<p>No template content</p>') 
        }}
      />
    </div>
  );
};

// Field Properties Panel Component
const FieldPropertiesPanel: React.FC<{
  field: TemplateField;
  onUpdateField: (updates: Partial<TemplateField>) => void;
  onDeleteField: () => void;
}> = ({ field, onUpdateField, onDeleteField }) => {
  return (
    <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Field Properties</h3>
        <p className="text-sm text-gray-600">{field.label}</p>
      </div>

      <div className="p-4 space-y-4">
        {/* Basic Properties */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Label
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={field.label}
            onChange={(e) => onUpdateField({ label: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Field Name
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={field.name}
            onChange={(e) => onUpdateField({ name: e.target.value })}
          />
        </div>

        {/* Position & Size */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">X</label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={field.position.x}
              onChange={(e) => onUpdateField({
                position: { ...field.position, x: parseInt(e.target.value) }
              })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Y</label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={field.position.y}
              onChange={(e) => onUpdateField({
                position: { ...field.position, y: parseInt(e.target.value) }
              })}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={field.position.width}
              onChange={(e) => onUpdateField({
                position: { ...field.position, width: parseInt(e.target.value) }
              })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={field.position.height}
              onChange={(e) => onUpdateField({
                position: { ...field.position, height: parseInt(e.target.value) }
              })}
            />
          </div>
        </div>

        {/* Style Properties */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Font Size
          </label>
          <input
            type="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={field.style.fontSize}
            onChange={(e) => onUpdateField({
              style: { ...field.style, fontSize: parseInt(e.target.value) }
            })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Font Weight
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={field.style.fontWeight}
            onChange={(e) => onUpdateField({
              style: { ...field.style, fontWeight: e.target.value }
            })}
          >
            <option value="normal">Normal</option>
            <option value="bold">Bold</option>
            <option value="lighter">Lighter</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Text Color
          </label>
          <input
            type="color"
            className="w-full h-10 border border-gray-300 rounded-md"
            value={field.style.color}
            onChange={(e) => onUpdateField({
              style: { ...field.style, color: e.target.value }
            })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Text Align
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={field.style.textAlign}
            onChange={(e) => onUpdateField({
              style: { ...field.style, textAlign: e.target.value }
            })}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
            <option value="justify">Justify</option>
          </select>
        </div>

        {/* Actions */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={onDeleteField}
            className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white 
                     rounded-md transition-colors flex items-center justify-center"
          >
            <FiTrash2 className="w-4 h-4 mr-2" />
            Delete Field
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateBuilder; 